import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useHealth } from '../context/HealthContext';
import axios from '../services/api';
import { API } from '../services/api';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import SendEmailModal from '../components/shared/SendEmailModal';
import TimelineSection from '../components/TimelineSection';
import {
  Spinner, Pill, Heartbeat, FileText,
  CalendarCheck, CaretUp, CaretDown, Minus, Clock,
  Stethoscope, Eye, Lightning,
  ArrowRight, Waveform, Warning,
  Plus, PencilSimple, Trash, X, Check, MagnifyingGlass,
  DownloadSimple, FilePdf, Export, EnvelopeSimple,
  ShieldCheck, Heart, UserCircle, CaretRight, CheckCircle,
  Target, Circle, SunHorizon, ListChecks, Notebook, ChatCircleText
} from '@phosphor-icons/react';
import { MainLayout } from '../components/shared/MainLayout';
// Sprint C — Health intelligence layer
import { DriversSection } from '../components/health/DriversSection';
import { DoctorQuestionCard } from '../components/health/DoctorQuestionCard';
import { ReportStatusCard } from '../components/health/ReportStatusCard';


/* ═══════════════════════════════════════
   STATUS HELPERS
   ═══════════════════════════════════════ */

/** Signal severity: prefers explicit severity field, falls back to confidence */
const signalLevel = (s) => s.severity || s.confidence || 'low';

const deriveStatus = (safetySignals) => {
  if (!safetySignals || safetySignals.length === 0) return 'stable';
  if (safetySignals.some(s => signalLevel(s) === 'high')) return 'attention';
  if (safetySignals.some(s => signalLevel(s) === 'moderate')) return 'monitor';
  return 'stable';
};

const STATUS_CONFIG = {
  stable: { label: 'Stable', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: CheckCircle, iconColor: 'text-emerald-500' },
  monitor: { label: 'Monitor', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', icon: Eye, iconColor: 'text-amber-500' },
  attention: { label: 'Needs attention', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', icon: Warning, iconColor: 'text-red-500' },
};

const prioritiseSignals = (signals, limit = 3) => {
  const order = { high: 0, moderate: 1, low: 2 };
  return [...signals]
    .sort((a, b) => {
      const diff = (order[signalLevel(a)] ?? 3) - (order[signalLevel(b)] ?? 3);
      if (diff !== 0) return diff;
      return (b.created_at || '').localeCompare(a.created_at || '');
    })
    .slice(0, limit);
};


/* ═══════════════════════════════════════
   DAILY BRAIN — "TODAY" TOP SECTION
   ═══════════════════════════════════════ */

const BRAIN_STATUS = {
  stable:    { label: 'Everything looks good', bg: 'bg-[#F0FAF6]', border: 'border-[#C6E7D9]', color: 'text-[#235347]' },
  monitor:   { label: 'A few things to watch', bg: 'bg-amber-50/70', border: 'border-amber-200', color: 'text-amber-700' },
  attention: { label: 'Needs your attention',  bg: 'bg-red-50/60',   border: 'border-red-200',  color: 'text-red-700' },
};

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const isToday = (d) => { const n = new Date(); return d.getDate() === n.getDate() && d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear(); };
const isTomorrow = (d) => { const t = new Date(); t.setDate(t.getDate() + 1); return d.getDate() === t.getDate() && d.getMonth() === t.getMonth() && d.getFullYear() === t.getFullYear(); };
const formatShortDate = (d) => d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });

/** Pick ONE primary item + ONE secondary (muted) context item */
const pickPrimary = (safetySignals, upcomingAppts, planItems, activeCourses = []) => {
  const sevOrder = { high: 0, moderate: 1, low: 2 };
  const sortedSignals = [...safetySignals].sort((a, b) => (sevOrder[signalLevel(a)] ?? 3) - (sevOrder[signalLevel(b)] ?? 3));
  const highSignal = sortedSignals.find(s => signalLevel(s) === 'high');
  const anySignal = sortedSignals[0];
  const inProgressCourse = (activeCourses || []).find(c => c.status !== 'completed');
  const topPlan = planItems[0];
  const soonAppt = [...upcomingAppts].sort((a, b) => new Date(a.date || a.scheduled_date) - new Date(b.date || b.scheduled_date))[0];

  let primary = null;
  let secondary = null;

  // Priority: HIGH signal → course step → plan item → moderate signal → appointment → low signal
  if (highSignal) {
    const short = highSignal.message.length > 80 ? highSignal.message.slice(0, 80).replace(/\s+\S*$/, '') + '...' : highSignal.message;
    primary = { source: 'signal', title: short, explain: highSignal.suggestion || 'Worth reviewing when you have a moment.', cta: 'Review', action: 'signal', reason: `Linked to your ${highSignal.category || 'health'} signals` };
  } else if (inProgressCourse) {
    const total = (inProgressCourse.sections || []).length;
    const step = inProgressCourse.current_step || 0;
    const short = (inProgressCourse.title || inProgressCourse.topic || '').slice(0, 55);
    const currentHeading = (inProgressCourse.sections || [])[step]?.heading || '';
    primary = { source: 'course', title: `Continue: ${short}`, explain: `Step ${step + 1} of ${total}${currentHeading ? ` — ${currentHeading}` : ''} is relevant today`, cta: 'Continue', action: 'course', courseId: inProgressCourse.id, reason: 'From your active learning' };
  } else if (topPlan) {
    const short = topPlan.text.length > 80 ? topPlan.text.slice(0, 80).replace(/\s+\S*$/, '') + '...' : topPlan.text;
    const fromCourse = topPlan.topic_title ? `From: ${topPlan.topic_title}` : 'From your learning plan';
    primary = { source: 'plan', title: short, explain: fromCourse, cta: 'Start', action: 'plan', reason: topPlan.topic_title ? `From your course: ${topPlan.topic_title}` : 'From your learning plan' };
  } else if (anySignal && signalLevel(anySignal) === 'moderate') {
    const short = anySignal.message.length > 80 ? anySignal.message.slice(0, 80).replace(/\s+\S*$/, '') + '...' : anySignal.message;
    primary = { source: 'signal', title: short, explain: anySignal.suggestion || 'Something to keep an eye on.', cta: 'Review', action: 'signal', reason: `Linked to your ${anySignal.category || 'health'} data` };
  } else if (soonAppt) {
    const d = new Date(soonAppt.date || soonAppt.scheduled_date);
    const label = soonAppt.title || soonAppt.reason || soonAppt.doctor_name || 'Appointment';
    const dayLabel = isToday(d) ? 'today' : isTomorrow(d) ? 'tomorrow' : formatShortDate(d);
    primary = { source: 'appointment', title: `${label} — ${dayLabel}`, explain: soonAppt.doctor_name ? `With ${soonAppt.doctor_name}` : 'Coming up soon', cta: 'Prepare', action: 'appointment', reason: 'From your upcoming appointments' };
  } else if (anySignal) {
    const short = anySignal.message.length > 80 ? anySignal.message.slice(0, 80).replace(/\s+\S*$/, '') + '...' : anySignal.message;
    primary = { source: 'signal', title: short, explain: anySignal.suggestion || 'Just keeping you informed.', cta: 'Review', action: 'signal', reason: 'Based on your recent activity' };
  }

  // Secondary: pick first non-primary item (appointment or plan, muted)
  if (primary?.source !== 'appointment' && soonAppt) {
    const d = new Date(soonAppt.date || soonAppt.scheduled_date);
    const label = soonAppt.title || soonAppt.reason || 'Appointment';
    const dayLabel = isToday(d) ? 'today' : isTomorrow(d) ? 'tomorrow' : formatShortDate(d);
    secondary = { text: `${label} — ${dayLabel}`, icon: CalendarCheck };
  } else if (primary?.source !== 'plan' && topPlan) {
    const short = topPlan.text.length > 50 ? topPlan.text.slice(0, 50).replace(/\s+\S*$/, '') + '...' : topPlan.text;
    secondary = { text: short, icon: Target };
  }

  return { primary, secondary };
};

const CTA_ICON = { signal: Eye, course: Notebook, plan: ListChecks, appointment: CalendarCheck };

const REINFORCEMENT_LINES = [
  "You're staying on top of things",
  "You're building a clearer picture",
  "You're making progress",
  "You're taking care of yourself",
];

const getReinforcementLine = (progress) => {
  if (!progress) return null;
  const total = (progress.total_actions_done || 0) + (progress.week?.check_ins || 0);
  if (total === 0) return null;
  return REINFORCEMENT_LINES[total % REINFORCEMENT_LINES.length];
};

const MOOD_OPTIONS = [
  { label: 'Feeling stable', value: 'Feeling stable today', color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  { label: 'A bit off', value: 'Feeling a bit off today', color: 'bg-amber-50 border-amber-200 text-amber-700' },
  { label: 'Not great', value: 'Not feeling great today', color: 'bg-red-50 border-red-200 text-red-600' },
];

const DailyBrainSection = ({ status, safetySignals, upcomingAppts, planItems, activeCourses, loading, userName, onNavigate, onCheckIn, progress }) => {
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkInText, setCheckInText] = useState('');
  const [checkInSaving, setCheckInSaving] = useState(false);
  const [checkInDone, setCheckInDone] = useState(false);

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 animate-pulse" data-testid="daily-brain-loading">
        <div className="h-4 bg-slate-200 rounded w-36 mb-4" />
        <div className="h-5 bg-slate-100 rounded w-48 mb-3" />
        <div className="h-3 bg-slate-100 rounded w-40" />
      </div>
    );
  }

  const cfg = BRAIN_STATUS[status] || BRAIN_STATUS.stable;
  const { primary, secondary } = pickPrimary(safetySignals, upcomingAppts, planItems, activeCourses);
  const greeting = getGreeting();
  const firstName = userName ? userName.split(' ')[0] : '';
  const CtaIcon = primary ? (CTA_ICON[primary.source] || ArrowRight) : Heart;

  const handleCta = () => {
    if (!primary || !onNavigate) return;
    if (primary.action === 'course') onNavigate('/learn', { state: { resumeCourseId: primary.courseId } });
    else if (primary.action === 'plan') onNavigate('/learn');
    else if (primary.action === 'signal') { /* scroll down handled by existing page */ }
    else if (primary.action === 'appointment') onNavigate('/health');
  };

  const handleCheckInSubmit = async (moodText) => {
    const text = moodText || checkInText.trim();
    if (!text || checkInSaving) return;
    setCheckInSaving(true);
    try {
      if (onCheckIn) await onCheckIn(text);
      setCheckInText('');
      setCheckInOpen(false);
      setCheckInDone(true);
    } finally {
      setCheckInSaving(false);
    }
  };

  const reinforcement = getReinforcementLine(progress);
  const streak = progress?.streak || 0;
  const lastCheckin = progress?.last_checkin || '';
  const hoursSinceCheckin = lastCheckin
    ? Math.floor((Date.now() - new Date(lastCheckin).getTime()) / 3600000)
    : 999;
  const showReturnTrigger = hoursSinceCheckin >= 24 && !checkInDone;

  return (
    <section className={`rounded-2xl border ${cfg.border} ${cfg.bg} p-6`} data-testid="daily-brain-section">
      {/* Greeting + Status */}
      <p className="text-sm text-slate-500 mb-1" data-testid="daily-brain-greeting">
        {greeting}{firstName ? `, ${firstName}` : ''}
      </p>
      {reinforcement && (
        <p className="text-xs text-[#235347]/60 mb-1.5" data-testid="daily-brain-reinforcement">{reinforcement}</p>
      )}
      <p className={`text-lg font-semibold ${cfg.color} leading-snug mb-6`} data-testid="daily-brain-status">
        {cfg.label}
      </p>

      {/* ONE Primary Card */}
      {primary ? (
        <div className="bg-white/70 rounded-xl border border-white/80 p-4 mb-5" data-testid="daily-brain-primary">
          <p className="text-sm font-medium text-[#0F172A] leading-snug mb-1.5" data-testid="daily-brain-primary-title">{primary.title}</p>
          <p className="text-xs text-slate-500 leading-relaxed mb-3">{primary.explain}</p>
          <button
            onClick={handleCta}
            className="inline-flex items-center gap-1.5 bg-[#235347] text-white rounded-lg px-4 py-2.5 text-xs font-medium hover:bg-[#1A3F36] transition-colors min-h-[40px]"
            data-testid="daily-brain-cta"
          >
            <CtaIcon size={14} weight="bold" />
            {primary.cta}
          </button>
          {primary.reason && (
            <p className="text-[11px] text-slate-400 mt-3" data-testid="daily-brain-why">
              {primary.reason}
            </p>
          )}
        </div>
      ) : (
        <div className="bg-white/70 rounded-xl border border-white/80 p-4 mb-5" data-testid="daily-brain-primary">
          <p className="text-sm text-slate-500 leading-relaxed">Nothing specific right now. Keep tracking how you feel.</p>
        </div>
      )}

      {/* Secondary context (muted) */}
      {secondary && (
        <div className="flex items-center gap-2 px-1 mb-5" data-testid="daily-brain-secondary">
          <secondary.icon size={13} className="text-slate-400" weight="duotone" />
          <p className="text-xs text-slate-400">{secondary.text}</p>
        </div>
      )}

      {/* Progress + Weekly snapshot */}
      {progress && (progress.recent_completions?.length > 0 || progress.week?.actions_done > 0 || progress.week?.check_ins > 0 || progress.week?.courses_completed > 0) && (
        <div className="bg-white/50 rounded-xl border border-white/60 p-4 mb-5" data-testid="daily-brain-progress">
          {progress.recent_completions?.length > 0 && (
            <div className="mb-3">
              <p className="text-[11px] font-semibold text-[#235347] uppercase tracking-wider mb-2">You're making progress</p>
              <div className="space-y-2">
                {progress.recent_completions.slice(0, 2).map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle size={12} className="text-emerald-400 flex-shrink-0" weight="fill" />
                    <p className="text-xs text-slate-500 truncate">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(progress.week?.actions_done > 0 || progress.week?.check_ins > 0) && (
            <div className="flex items-center gap-3 text-[11px] text-slate-400" data-testid="daily-brain-weekly">
              {progress.week.actions_done > 0 && <span>{progress.week.actions_done} step{progress.week.actions_done !== 1 ? 's' : ''} this week</span>}
              {progress.week.actions_done > 0 && progress.week.check_ins > 0 && <span className="text-slate-300">·</span>}
              {progress.week.check_ins > 0 && <span>{progress.week.check_ins} check-in{progress.week.check_ins !== 1 ? 's' : ''}</span>}
              {progress.week.courses_completed > 0 && <><span className="text-slate-300">·</span><span>{progress.week.courses_completed} course{progress.week.courses_completed !== 1 ? 's' : ''} done</span></>}
            </div>
          )}
        </div>
      )}

      {/* Check-in */}
      {showReturnTrigger && !checkInOpen && !checkInDone && (
        <div className="bg-white/60 rounded-xl border border-white/70 p-3.5 mb-4" data-testid="daily-brain-return-trigger">
          <p className="text-xs text-slate-500 leading-snug">It's been a little while — want to check in with yourself?</p>
        </div>
      )}

      {checkInDone ? (
        <div className="flex items-center gap-2 bg-white/50 border border-white/60 rounded-xl px-3.5 py-3" data-testid="daily-brain-checkin-done">
          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
          <p className="text-xs text-slate-500">Checked in{streak > 1 ? ` · ${streak} days in a row` : ''}</p>
        </div>
      ) : !checkInOpen ? (
        <div>
          <button
            onClick={() => setCheckInOpen(true)}
            className="w-full flex items-center justify-center gap-2 bg-white/50 border border-white/60 rounded-xl px-3.5 py-3 text-xs text-slate-400 hover:text-slate-600 hover:bg-white/80 transition-colors"
            data-testid="daily-brain-checkin-btn"
          >
            <Heart size={13} weight="duotone" />
            Check in with yourself
          </button>
          {streak > 1 && (
            <p className="text-[11px] text-slate-400 text-center mt-2" data-testid="daily-brain-streak">{streak} days in a row</p>
          )}
        </div>
      ) : (
        <div className="bg-white/80 rounded-xl border border-white/80 p-4" data-testid="daily-brain-checkin-form">
          <p className="text-xs text-slate-500 mb-3">Check in with yourself</p>
          {/* Mood quick-tap */}
          <div className="flex gap-2 mb-3" data-testid="daily-brain-mood-options">
            {MOOD_OPTIONS.map((mood, i) => (
              <button
                key={i}
                onClick={() => handleCheckInSubmit(mood.value)}
                disabled={checkInSaving}
                className={`flex-1 text-[11px] font-medium py-2.5 rounded-lg border transition-colors min-h-[40px] ${mood.color} disabled:opacity-50`}
                data-testid={`mood-option-${i}`}
              >
                {mood.label}
              </button>
            ))}
          </div>
          {/* Optional text */}
          <div className="flex gap-2">
            <input
              value={checkInText}
              onChange={(e) => setCheckInText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCheckInSubmit()}
              placeholder="Or add a note..."
              className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-[#235347]/20"
              style={{ fontSize: '16px' }}
              data-testid="daily-brain-checkin-input"
            />
            <button
              onClick={() => handleCheckInSubmit()}
              disabled={!checkInText.trim() || checkInSaving}
              className="bg-[#235347] text-white rounded-lg px-3.5 py-2.5 text-xs font-medium hover:bg-[#1A3F36] disabled:opacity-50 transition-colors min-w-[48px]"
              data-testid="daily-brain-checkin-submit"
            >
              {checkInSaving ? <Spinner size={12} className="animate-spin" /> : 'Log'}
            </button>
          </div>
          <button onClick={() => setCheckInOpen(false)} className="text-[11px] text-slate-400 mt-2 hover:text-slate-600">Cancel</button>
        </div>
      )}
    </section>
  );
};


/* ═══════════════════════════════════════
   SECTION 1 — YOUR HEALTH RIGHT NOW
   ═══════════════════════════════════════ */
const HealthStatusCard = ({ status, topSignals, conditionCount, medCount, loading }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.stable;
  const StatusIcon = cfg.icon;

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-5 animate-pulse">
        <div className="h-5 bg-slate-200 rounded w-1/3 mb-3" />
        <div className="h-3 bg-slate-100 rounded w-2/3 mb-2" />
        <div className="h-3 bg-slate-100 rounded w-1/2" />
      </div>
    );
  }

  return (
    <div className={`rounded-xl border ${cfg.border} ${cfg.bg} p-5`} data-testid="health-status-card">
      <div className="flex items-center gap-2.5 mb-3">
        <StatusIcon size={20} className={cfg.iconColor} weight="duotone" />
        <span className={`text-base font-semibold ${cfg.color}`} data-testid="health-status-label">{cfg.label}</span>
      </div>

      {topSignals.length > 0 ? (
        <div className="space-y-2.5 mb-3">
          {topSignals.slice(0, 2).map((sig, i) => (
            <div key={sig.id || i} className="flex items-start gap-2">
              <span className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                signalLevel(sig) === 'high' ? 'bg-red-400' : signalLevel(sig) === 'moderate' ? 'bg-amber-400' : 'bg-slate-300'
              }`} />
              <p className="text-sm text-slate-700 leading-relaxed line-clamp-2">{sig.message}</p>
            </div>
          ))}
          {topSignals.length > 2 && (
            <p className="text-[11px] text-slate-400 pl-3.5">+{topSignals.length - 2} more signal{topSignals.length - 2 !== 1 ? 's' : ''}</p>
          )}
        </div>
      ) : (
        <p className="text-sm text-slate-500 mb-3 leading-relaxed">
          {conditionCount > 0 || medCount > 0
            ? <>Stable today — Kindra is tracking your {conditionCount > 0 ? `${conditionCount} condition${conditionCount !== 1 ? 's' : ''}` : ''}{conditionCount > 0 && medCount > 0 ? ', ' : ''}{medCount > 0 ? `${medCount} medication${medCount !== 1 ? 's' : ''}` : ''} and recent symptoms.</>
            : <>Nothing flagged right now. Add a condition, medication, or upload a recent report so Kindra has more to work with.</>}
        </p>
      )}

      {topSignals.length > 0 && topSignals[0].suggestion && (
        <div className="bg-white/60 rounded-lg px-3.5 py-2.5 border border-white/80">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Next step</p>
          <p className="text-sm text-slate-600 leading-relaxed line-clamp-2">{topSignals[0].suggestion}</p>
        </div>
      )}

      <p className="text-[11px] text-slate-400 mt-3">From your recent activity</p>
    </div>
  );
};


/* ═══════════════════════════════════════
   SECTION 2 — WHAT NEEDS ATTENTION
   ═══════════════════════════════════════ */
const AttentionCard = ({ signal, onDismiss }) => {
  const level = signalLevel(signal);
  const levelStyle = {
    high: 'border-red-200 bg-red-50/50',
    moderate: 'border-amber-200 bg-amber-50/50',
    low: 'border-slate-200 bg-slate-50/50',
  };

  return (
    <div
      className={`rounded-xl border p-4 ${levelStyle[level] || levelStyle.low}`}
      data-testid={`attention-card-${signal.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
              level === 'high' ? 'bg-red-100 text-red-600' :
              level === 'moderate' ? 'bg-amber-100 text-amber-600' :
              'bg-slate-100 text-slate-500'
            }`}>{level}</span>
            <span className="text-[10px] text-slate-400 uppercase tracking-wider">{signal.category}</span>
          </div>
          <p className="text-sm text-[#0F172A] leading-snug mb-1.5 line-clamp-3">{signal.message}</p>
          {signal.suggestion && (
            <p className="text-xs text-amber-700 font-medium line-clamp-2">{signal.suggestion}</p>
          )}
        </div>
        <button
          onClick={() => onDismiss(signal)}
          className="p-1.5 hover:bg-white/60 rounded-lg transition-colors flex-shrink-0"
          title="Dismiss"
          data-testid={`dismiss-signal-${signal.id}`}
        >
          <X size={14} className="text-slate-400" />
        </button>
      </div>
      <p className="text-[11px] text-slate-400 mt-2.5">Linked to your health data</p>
    </div>
  );
};


/* ═══════════════════════════════════════
   SECTION 3 — YOUR SETUP (Summary Cards)
   ═══════════════════════════════════════ */
const SetupCard = ({ icon: Icon, iconColor, label, count, items, onEdit, onAdd, testId }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testId}>
    <div className="flex items-center justify-between mb-2.5">
      <div className="flex items-center gap-2">
        <Icon size={16} className={iconColor} weight="duotone" />
        <span className="text-sm font-semibold text-[#0F172A]">{label}</span>
        <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${
          iconColor.includes('emerald') || iconColor.includes('[#235347]') ? 'bg-[#235347]/10 text-[#235347]' :
          iconColor.includes('blue') ? 'bg-blue-50 text-blue-600' :
          iconColor.includes('rose') ? 'bg-rose-50 text-rose-500' :
          'bg-slate-100 text-slate-500'
        }`}>{count}</span>
      </div>
      <div className="flex items-center gap-0.5">
        <button
          onClick={onAdd}
          className="p-2 min-w-[40px] min-h-[40px] flex items-center justify-center hover:bg-slate-50 rounded-lg transition-colors"
          data-testid={`${testId}-add-btn`}
        >
          <Plus size={16} className="text-slate-400" />
        </button>
      </div>
    </div>
    {items.length > 0 ? (
      <div className="space-y-1">
        {items.slice(0, 3).map((item, i) => (
          <div key={i} className="flex items-center gap-2 py-1">
            <span className="w-1 h-1 rounded-full bg-slate-300 flex-shrink-0" />
            <span className="text-xs text-slate-600 truncate">{item}</span>
          </div>
        ))}
        {items.length > 3 && (
          <button
            onClick={onEdit}
            className="text-[11px] text-slate-400 hover:text-slate-600 pl-3 pt-0.5 transition-colors"
            data-testid={`${testId}-view-all`}
          >
            View all {items.length} →
          </button>
        )}
      </div>
    ) : (
      <p className="text-xs text-slate-400 italic">None added yet</p>
    )}
  </div>
);

const CareTeamCard = ({ members, onManage }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="setup-care-team">
    <div className="flex items-center justify-between mb-2.5">
      <div className="flex items-center gap-2">
        <Stethoscope size={16} className="text-[#235347]" weight="duotone" />
        <span className="text-sm font-semibold text-[#0F172A]">Care Team</span>
        <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-[#235347]/10 text-[#235347]">{members.length}</span>
      </div>
      <button
        onClick={onManage}
        className="text-[11px] font-medium text-[#235347] hover:text-[#1A3F36] transition-colors flex items-center gap-0.5 min-h-[40px] px-2"
        data-testid="manage-care-team-btn"
      >
        Manage <CaretRight size={12} />
      </button>
    </div>
    {members.length > 0 ? (
      <div className="space-y-1.5">
        {members.slice(0, 3).map((m, i) => (
          <div key={i} className="flex items-center gap-2 py-0.5">
            <UserCircle size={14} className="text-slate-400" weight="duotone" />
            <span className="text-xs text-slate-600 truncate flex-1">{m.name}</span>
            <span className="text-[11px] text-slate-400">{m.role}</span>
          </div>
        ))}
        {members.length > 3 && (
          <p className="text-[10px] text-slate-400 pl-5">+{members.length - 3} more</p>
        )}
      </div>
    ) : (
      <button onClick={onManage} className="text-xs text-[#235347] font-medium hover:underline">
        Add your first clinician
      </button>
    )}
  </div>
);


/* ═══════════════════════════════════════
   REUSABLE BOTTOM SHEET MODAL
   ═══════════════════════════════════════ */
const BottomSheet = ({ title, onClose, children }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 bg-black/50 z-50 flex items-end"
    onClick={onClose}
  >
    <motion.div
      initial={{ y: '100%' }}
      animate={{ y: 0 }}
      exit={{ y: '100%' }}
      onClick={(e) => e.stopPropagation()}
      className="bg-white w-full rounded-t-3xl p-6 pb-24 max-h-[85vh] overflow-auto z-[60]"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-medium text-slate-900">{title}</h2>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="modal-close">
          <X size={24} />
        </button>
      </div>
      {children}
    </motion.div>
  </motion.div>
);


/* ═══════════════════════════════════════
   MEDICATION AUTOCOMPLETE
   ═══════════════════════════════════════ */
const MedicationAutocomplete = ({ value, onChange, onSelectForms }) => {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(!!value);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = useCallback((q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.length < 2) { setResults([]); setShowDropdown(false); return; }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/medications/search?q=${encodeURIComponent(q)}&limit=8`);
        setResults(data);
        setShowDropdown(data.length > 0);
      } catch { setResults([]); }
      setSearching(false);
    }, 200);
  }, []);

  const handleInputChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    setSelected(false);
    onChange(v);
    handleSearch(v);
  };

  const handleSelect = (item) => {
    const name = item.type === 'brand' ? item.display : item.name;
    setQuery(name);
    setSelected(true);
    setShowDropdown(false);
    onChange(name);
    if (item.forms && item.forms.length > 0 && onSelectForms) {
      onSelectForms(item.forms);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={query}
          onChange={handleInputChange}
          onFocus={() => { if (results.length > 0 && !selected) setShowDropdown(true); }}
          placeholder="Search medication name..."
          className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-4 py-3 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 transition-all"
          data-testid="medication-name-input"
          autoComplete="off"
        />
        {searching && (
          <Spinner size={14} className="absolute right-3.5 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />
        )}
      </div>

      <AnimatePresence>
        {showDropdown && results.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-auto"
            data-testid="medication-search-results"
          >
            {results.map((item, i) => (
              <button
                key={i}
                onClick={() => handleSelect(item)}
                className="w-full px-4 py-2.5 text-left hover:bg-blue-50 transition-colors border-b border-slate-100 last:border-0 flex items-center justify-between"
                data-testid={`medication-result-${i}`}
              >
                <div>
                  <p className="text-sm font-medium text-slate-800">{item.display}</p>
                  <p className="text-[10px] text-slate-400 capitalize">{item.category?.replace('_', ' ')}</p>
                </div>
                <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${
                  item.type === 'brand' ? 'bg-blue-50 text-blue-500' : 'bg-slate-100 text-slate-500'
                }`}>{item.type}</span>
              </button>
            ))}
            <div className="px-4 py-2 bg-slate-50 text-[10px] text-slate-400">
              Can't find yours? Type the full name manually.
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};


/* ═══════════════════════════════════════
   MEDICATION MODAL CONTENT
   ═══════════════════════════════════════ */
const MedModalContent = ({ formData, setFormData, handleSave, saving, mode }) => {
  const [suggestedForms, setSuggestedForms] = useState([]);

  return (
    <>
      <div className="space-y-4">
        <MedicationAutocomplete
          value={formData.name || ''}
          onChange={(name) => setFormData(prev => ({ ...prev, name }))}
          onSelectForms={(forms) => setSuggestedForms(forms)}
        />
        <div className="grid grid-cols-2 gap-3">
          <div className="relative">
            <input
              placeholder="Dosage (e.g. 10mg)"
              value={formData.dosage || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, dosage: e.target.value }))}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none"
              data-testid="medication-dosage-input"
            />
            {suggestedForms.length > 0 && !formData.dosage && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {suggestedForms.slice(0, 5).map((form, i) => (
                  <button
                    key={i}
                    onClick={() => setFormData(prev => ({ ...prev, dosage: form }))}
                    className="text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full hover:bg-blue-100 transition-colors"
                    data-testid={`dosage-suggestion-${i}`}
                  >
                    {form}
                  </button>
                ))}
              </div>
            )}
          </div>
          <select
            value={formData.frequency || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, frequency: e.target.value }))}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none"
            data-testid="medication-frequency-select"
          >
            <option value="">Frequency</option>
            <option value="once daily">Once daily</option>
            <option value="twice daily">Twice daily</option>
            <option value="three times daily">Three times daily</option>
            <option value="as needed">As needed</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        <input
          placeholder="What is this medication for? (e.g. blood pressure)"
          value={formData.purpose || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, purpose: e.target.value }))}
          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none"
          data-testid="medication-purpose-input"
        />
        <input
          type="date"
          placeholder="Start date"
          value={formData.start_date || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none"
          data-testid="medication-start-date-input"
        />
        <select
          value={formData.status || 'active'}
          onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none"
          data-testid="medication-status-select"
        >
          <option value="active">Active</option>
          <option value="discontinued">Discontinued</option>
          <option value="as_needed">As needed</option>
        </select>
        <textarea
          placeholder="Notes (optional)"
          value={formData.notes || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none min-h-[80px] resize-none"
          data-testid="medication-notes-input"
        />
      </div>
      <button
        onClick={handleSave}
        disabled={!formData.name || saving}
        className="w-full mt-6 bg-blue-600 text-white rounded-full px-6 py-3 font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="save-medication-btn"
      >
        {saving && <Spinner size={16} className="animate-spin" />}
        {mode === 'add' ? 'Add Medication' : 'Save Changes'}
      </button>
    </>
  );
};


/* ═══════════════════════════════════════
   FULL EDIT VIEW (Conditions / Meds / Symptoms)
   ═══════════════════════════════════════ */
const severityColor = (s) => {
  if (!s) return 'bg-slate-100 text-slate-500';
  const sl = s.toLowerCase();
  if (sl === 'severe') return 'bg-red-50 text-red-600';
  if (sl === 'moderate') return 'bg-amber-50 text-amber-600';
  if (sl === 'mild') return 'bg-emerald-50 text-emerald-700';
  return 'bg-slate-100 text-slate-500';
};


import { useSWRCache, peekSWRCache } from '../services/useSWRCache';

/* ═══════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════ */
const HealthPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { healthVersion, refreshHealthImmediate } = useHealth();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [careTeam, setCareTeam] = useState([]);
  const [planItems, setPlanItems] = useState([]);
  const [activeCourses, setActiveCourses] = useState([]);
  const [progressData, setProgressData] = useState(null);
  // Sprint C — enriched health intelligence (drivers, doctor_question, report_status)
  const [healthState, setHealthState] = useState(null);

  // Edit view state
  const [editSection, setEditSection] = useState(null); // 'conditions' | 'medications' | 'symptoms' | null

  // Tabs — Sprint B rebuild. Snapshot is the calm command-centre default.
  const [activeTab, setActiveTab] = useState('snapshot');
  // P1 Stage-14b — Snapshot must feel calm. Keep the default view to:
  //   • Status (Needs attention max 2 — already enforced inside HealthStatusCard)
  //   • Top 3 things Kindra is monitoring (already enforced via prioritiseSignals)
  //   • Recent reports (ReportStatusCard)
  //   • Ask Kindra about this (Understand-better CTA)
  // Everything more analytical (Drivers narrative, Doctor question, Next-steps
  // plan items) lives behind a quiet "View details" toggle so the Snapshot
  // never reads as a wall of warnings.
  const [showSnapshotDetails, setShowSnapshotDetails] = useState(false);

  // Modal state
  const [modal, setModal] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exporting, setExporting] = useState(null);
  // Stage 19 — user-authored health notes. Lives alongside conditions/meds in
  // the profile hub. Fetched via /api/notes and shown in the new Notes card.
  const [notes, setNotes] = useState([]);
  const fetchNotes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notes`);
      setNotes((data && data.notes) || []);
    } catch { /* ignore */ }
  }, []);

  // Stage-15 P0 — cache-first for the heaviest, render-blocking payload.
  // Returning to /health from anywhere else (chat, reports, profile) now
  // shows the previous record snapshot instantly while a background refetch
  // keeps it fresh. Skeleton flash is gone.
  const { data: recordData, refetch: refetchRecord } = useSWRCache('/health/record', { ttlMs: 30_000 });
  const record = recordData;
  // Local "loading" only true on FIRST EVER cache miss (zero data). On any
  // return-nav, recordData is already the previous snapshot.
  const [loading, setLoading] = useState(record == null);
  useEffect(() => {
    if (recordData != null && loading) setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordData]);
  // Compatibility shim — preserves existing call sites that expect a setter.
  const setRecord = () => { /* writes happen via mutation endpoints + refetchRecord */ };
  // Legacy alias used by mutation handlers throughout this file.
  const fetchRecord = refetchRecord;

  const fetchCareTeam = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/medical-team`);
      setCareTeam(data.filter(m => m.status !== 'past'));
    } catch { /* ignore */ }
  }, []);

  const fetchPlan = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/university/plan`);
      setPlanItems(data.filter(i => !i.done));
    } catch { /* ignore */ }
  }, []);

  const fetchActiveCourses = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/university/courses`);
      setActiveCourses((data || []).filter(c => c.status !== 'completed'));
    } catch { /* ignore */ }
  }, []);

  const fetchProgress = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/university/progress`);
      setProgressData(data);
    } catch { /* ignore */ }
  }, []);

  // Sprint C — pull /health/state for drivers, doctor_question, report_status.
  // Non-blocking; if it fails the Snapshot just hides those calm sections.
  const fetchHealthState = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/health/state`);
      setHealthState(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchCareTeam();
    fetchPlan();
    fetchActiveCourses();
    fetchProgress();
    fetchHealthState();
    fetchNotes();
  }, [fetchCareTeam, fetchPlan, fetchActiveCourses, fetchProgress, fetchHealthState, fetchNotes]);

  useEffect(() => {
    if (healthVersion > 0) {
      refetchRecord();
      fetchCareTeam();
      fetchPlan();
      fetchHealthState();
    }
  }, [healthVersion, fetchRecord, fetchCareTeam, fetchPlan, fetchHealthState]);

  // ─── CRUD handlers ───
  const openAdd = (type) => {
    const defaults = {
      condition: { name: '', severity: '', notes: '', status: 'active' },
      medication: { name: '', dosage: '', frequency: '', purpose: '', notes: '', status: 'active' },
      symptom: { name: '', severity: null, frequency: '', notes: '' },
      // Stage 19 — free-text health notes the user wants Kindra to remember.
      note: { text: '', category: 'other' },
    };
    setFormData(defaults[type] || {});
    setModal({ type, mode: 'add' });
  };

  const openEdit = (type, item) => {
    setFormData({ ...item });
    setModal({ type, mode: 'edit', id: item.id });
  };

  const handleExportPDF = async () => {
    setExporting('pdf');
    setShowExportMenu(false);
    try {
      const response = await axios.get(`${API}/export/doctor-summary`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Kindra_Health_Summary_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Health summary downloaded');
    } catch {
      toast.error('Failed to generate summary');
    } finally {
      setExporting(null);
    }
  };

  const handleExportJSON = async () => {
    setExporting('json');
    setShowExportMenu(false);
    try {
      const { data } = await axios.get(`${API}/export/health-data`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Kindra_Health_Export_${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Health data exported');
    } catch {
      toast.error('Failed to export data');
    } finally {
      setExporting(null);
    }
  };

  const handleSave = async () => {
    if (!modal) return;
    setSaving(true);
    try {
      // Stage 19 — notes go to /api/notes; everything else goes to its own
      // collection endpoint. Success copy lands as a toast so the user sees
      // clear confirmation that Kindra has recorded the update.
      if (modal.type === 'note') {
        if (modal.mode === 'add') {
          await axios.post(`${API}/notes`, formData);
          toast.success('Saved. Kindra can now use this when helping you.');
        } else {
          await axios.put(`${API}/notes/${modal.id}`, formData);
          toast.success('Note updated');
        }
        setModal(null);
        await fetchNotes();
        refreshHealthImmediate();
        return;
      }
      const endpoint = modal.type === 'condition' ? 'conditions' : modal.type === 'medication' ? 'medications' : 'symptoms';
      if (modal.mode === 'add') {
        await axios.post(`${API}/${endpoint}`, formData);
        toast.success(`${modal.type.charAt(0).toUpperCase() + modal.type.slice(1)} added`);
      } else {
        await axios.put(`${API}/${endpoint}/${modal.id}`, formData);
        toast.success(`${modal.type.charAt(0).toUpperCase() + modal.type.slice(1)} updated`);
      }
      setModal(null);
      await fetchRecord();
      refreshHealthImmediate();
    } catch (err) {
      const msg = err?.response?.data?.detail || `Failed to save ${modal.type}`;
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm(`Delete this ${type}?`)) return;
    try {
      // Stage 19 — notes live under /api/notes; everything else under its
      // own collection endpoint. Same confirm/toast pattern across types.
      if (type === 'note') {
        await axios.delete(`${API}/notes/${id}`);
        toast.success('Note deleted');
        await fetchNotes();
        refreshHealthImmediate();
        return;
      }
      const endpoint = type === 'condition' ? 'conditions' : type === 'medication' ? 'medications' : 'symptoms';
      await axios.delete(`${API}/${endpoint}/${id}`);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted`);
      await fetchRecord();
      refreshHealthImmediate();
    } catch (err) {
      const msg = err?.response?.data?.detail || `Failed to delete ${type}`;
      toast.error(msg);
    }
  };

  const handleCompletePlanItem = async (itemId) => {
    setPlanItems(prev => prev.filter(i => i.id !== itemId));
    try {
      await axios.patch(`${API}/university/plan/${itemId}`, { done: true });
    } catch {
      fetchPlan();
    }
  };

  const handleRemovePlanItem = async (itemId) => {
    setPlanItems(prev => prev.filter(i => i.id !== itemId));
    try {
      await axios.delete(`${API}/university/plan/${itemId}`);
    } catch {
      fetchPlan();
    }
  };


  const handleDismissSignal = async (signal) => {
    setRecord(prev => {
      if (!prev?.intelligence?.safety_signals) return prev;
      return {
        ...prev,
        intelligence: {
          ...prev.intelligence,
          safety_signals: prev.intelligence.safety_signals.filter(s => s.id !== signal.id),
        },
      };
    });
    try {
      await axios.post(`${API}/health/safety-signals/dismiss`, {
        signal_id: signal.id,
        message: signal.message || '',
      });
    } catch {
      fetchRecord();
    }
  };

  // ─── Derived data ───
  const {
    conditions = [],
    medications = [],
    symptoms = [],
    appointments: appts = [],
    intelligence = {},
  } = record || {};

  const activeConditions = conditions.filter(c => c.status === 'active' || !c.status);
  const safetySignals = (intelligence?.safety_signals || []).filter(s => !s.dismissed);
  const status = deriveStatus(safetySignals);
  const topSignals = prioritiseSignals(safetySignals, 3);

  // Upcoming appointments (future only)
  const now = new Date();
  const upcomingAppointments = appts.filter(a => {
    const d = new Date(a.date || a.scheduled_date);
    return d >= new Date(now.getFullYear(), now.getMonth(), now.getDate());
  });

  // Setup summaries
  const conditionItems = activeConditions.map(c => `${c.name}${c.severity ? ` (${c.severity})` : ''}`);
  const medicationItems = medications.map(m => `${m.name}${m.dosage ? ` ${m.dosage}` : ''}${m.purpose ? ` — ${m.purpose}` : ''}`);
  const symptomItems = symptoms.map(s => `${s.name}${s.frequency ? ` (${s.frequency})` : ''}`);

  // ─── Edit section full view ───
  if (editSection) {
    const sectionConfig = {
      conditions: { label: 'Conditions', items: activeConditions, color: 'text-[#235347]', addType: 'condition' },
      medications: { label: 'Medications', items: medications, color: 'text-blue-600', addType: 'medication' },
      symptoms: { label: 'Symptoms', items: symptoms, color: 'text-rose-500', addType: 'symptom' },
    };
    const sec = sectionConfig[editSection];

    return (
      <MainLayout>
        <div className="max-w-lg mx-auto pb-24">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button onClick={() => setEditSection(null)} className="p-1 hover:bg-slate-100 rounded-lg" data-testid="back-from-edit">
                <X size={18} className="text-slate-500" />
              </button>
              <h2 className="text-lg font-semibold text-[#0F172A]">{sec.label}</h2>
            </div>
            <button
              onClick={() => openAdd(sec.addType)}
              className={`flex items-center gap-1 text-xs font-medium ${sec.color}`}
              data-testid={`add-${sec.addType}-btn`}
            >
              <Plus size={14} /> Add
            </button>
          </div>

          {sec.items.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
              <p className="text-sm text-slate-400">No {sec.label.toLowerCase()} recorded yet.</p>
              <button onClick={() => openAdd(sec.addType)} className={`mt-3 text-sm ${sec.color} font-medium hover:underline`}>
                Add your first {sec.addType}
              </button>
            </div>
          ) : editSection === 'conditions' ? (
            <div className="space-y-3">
              {sec.items.map((c, i) => (
                <div key={c.id || i} className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-semibold text-[#0F172A]">{c.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {c.severity && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${severityColor(c.severity)} uppercase`}>{c.severity}</span>}
                        <span className="text-[10px] text-slate-400 capitalize">{c.status || 'active'}</span>
                      </div>
                      {c.knowledge_excerpt && <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">{c.knowledge_excerpt}</p>}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openEdit('condition', c)} className="p-1.5 hover:bg-slate-100 rounded-md" data-testid={`edit-condition-${c.id}`}><PencilSimple size={14} className="text-slate-400" /></button>
                      <button onClick={() => handleDelete('condition', c.id)} className="p-1.5 hover:bg-red-50 rounded-md" data-testid={`delete-condition-${c.id}`}><Trash size={14} className="text-red-400" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : editSection === 'medications' ? (
            <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
              {sec.items.map((m, i) => (
                <div key={m.id || i} className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-[#0F172A]">{m.name}</p>
                    {(m.dosage || m.frequency) && <p className="text-[10px] text-slate-400">{m.dosage} {m.frequency}</p>}
                    {m.purpose && <p className="text-[10px] text-blue-400 mt-0.5">{m.purpose}</p>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit('medication', m)} className="p-1.5 hover:bg-slate-100 rounded-md" data-testid={`edit-medication-${m.id}`}><PencilSimple size={14} className="text-slate-400" /></button>
                    <button onClick={() => handleDelete('medication', m.id)} className="p-1.5 hover:bg-red-50 rounded-md" data-testid={`delete-medication-${m.id}`}><Trash size={14} className="text-red-400" /></button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {sec.items.map((s, i) => (
                <div key={s.id || i} className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Heartbeat size={14} className="text-rose-400" weight="duotone" />
                    <span className="text-xs font-medium text-[#0F172A]">{s.name}</span>
                    {s.frequency && <span className="text-[10px] text-slate-400">({s.frequency})</span>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit('symptom', s)} className="p-1.5 hover:bg-slate-100 rounded-md" data-testid={`edit-symptom-${s.id}`}><PencilSimple size={14} className="text-slate-400" /></button>
                    <button onClick={() => handleDelete('symptom', s.id)} className="p-1.5 hover:bg-red-50 rounded-md" data-testid={`delete-symptom-${s.id}`}><Trash size={14} className="text-red-400" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modals inside edit view */}
        <AnimatePresence>
          {modal && renderModal()}
        </AnimatePresence>
      </MainLayout>
    );
  }

  // Helper to render modals (used in both views)
  function renderModal() {
    if (!modal) return null;
    if (modal.type === 'condition') {
      return (
        <BottomSheet title={modal.mode === 'add' ? 'Add Condition' : 'Edit Condition'} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <input placeholder="Condition name *" value={formData.name || ''} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none" data-testid="condition-name-input" />
            <select value={formData.severity || ''} onChange={(e) => setFormData(prev => ({ ...prev, severity: e.target.value }))} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none" data-testid="condition-severity-select">
              <option value="">Select severity</option>
              <option value="mild">Mild</option>
              <option value="moderate">Moderate</option>
              <option value="severe">Severe</option>
            </select>
            <input type="date" value={formData.diagnosed_date || ''} onChange={(e) => setFormData(prev => ({ ...prev, diagnosed_date: e.target.value }))} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none" data-testid="condition-date-input" />
            <select value={formData.status || 'active'} onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none" data-testid="condition-status-select">
              <option value="active">Active</option>
              <option value="managed">Managed</option>
              <option value="resolved">Resolved</option>
            </select>
            <textarea placeholder="Notes (optional)" value={formData.notes || ''} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none min-h-[80px] resize-none" data-testid="condition-notes-input" />
          </div>
          <button onClick={handleSave} disabled={!formData.name || saving} className="w-full mt-6 bg-[#235347] text-white rounded-full px-6 py-3 font-medium hover:bg-[#1A3F36] disabled:opacity-50 flex items-center justify-center gap-2" data-testid="save-condition-btn">
            {saving && <Spinner size={16} className="animate-spin" />}
            {modal.mode === 'add' ? 'Add Condition' : 'Save Changes'}
          </button>
        </BottomSheet>
      );
    }
    if (modal.type === 'medication') {
      return (
        <BottomSheet title={modal.mode === 'add' ? 'Add Medication' : 'Edit Medication'} onClose={() => setModal(null)}>
          <MedModalContent formData={formData} setFormData={setFormData} handleSave={handleSave} saving={saving} mode={modal.mode} />
        </BottomSheet>
      );
    }
    if (modal.type === 'symptom') {
      return (
        <BottomSheet title={modal.mode === 'add' ? 'Add Symptom' : 'Edit Symptom'} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <input placeholder="Symptom name *" value={formData.name || ''} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none" data-testid="symptom-name-input" />
            <select value={formData.frequency || ''} onChange={(e) => setFormData(prev => ({ ...prev, frequency: e.target.value }))} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none" data-testid="symptom-frequency-select">
              <option value="">How often?</option>
              <option value="constant">Constant</option>
              <option value="daily">Daily</option>
              <option value="several times a week">Several times a week</option>
              <option value="weekly">Weekly</option>
              <option value="occasional">Occasional</option>
              <option value="rare">Rare</option>
            </select>
            <div>
              <label className="text-sm text-slate-500 mb-2 block">Severity (1-10)</label>
              <input type="range" min={1} max={10} value={formData.severity || 5} onChange={(e) => setFormData(prev => ({ ...prev, severity: parseInt(e.target.value) }))} className="w-full h-2 bg-slate-200 rounded-full appearance-none cursor-pointer accent-rose-500" data-testid="symptom-severity-range" />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>Mild</span>
                <span className="text-rose-500 font-medium">{formData.severity || 5}/10</span>
                <span>Severe</span>
              </div>
            </div>
            <textarea placeholder="Notes (optional — triggers, patterns, etc.)" value={formData.notes || ''} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none min-h-[80px] resize-none" data-testid="symptom-notes-input" />
          </div>
          <button onClick={handleSave} disabled={!formData.name || saving} className="w-full mt-6 bg-rose-500 text-white rounded-full px-6 py-3 font-medium hover:bg-rose-600 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="save-symptom-btn">
            {saving && <Spinner size={16} className="animate-spin" />}
            {modal.mode === 'add' ? 'Add Symptom' : 'Save Changes'}
          </button>
        </BottomSheet>
      );
    }
    // Stage 19 — "Tell Kindra something important" modal. Single textarea +
    // optional category dropdown + save. Keeps onboarding tone (warm, low-
    // friction). Categories are soft — "other" is the default; the user
    // doesn't have to think about taxonomy.
    if (modal.type === 'note') {
      return (
        <BottomSheet title={modal.mode === 'add' ? 'Tell Kindra something important' : 'Edit note'} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <p className="text-[13px] text-slate-500 leading-relaxed">
              Add any context that doesn't fit into conditions or medications — a recent change, a doctor comment, a symptom pattern, something you're worried about. Kindra will use this next time you ask.
            </p>
            <textarea
              placeholder="e.g. I've been more tired than usual for 3 weeks. My GP mentioned low iron."
              value={formData.text || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, text: e.target.value.slice(0, 1000) }))}
              rows={5}
              maxLength={1000}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none min-h-[120px] resize-none text-[14px] leading-relaxed"
              data-testid="note-text-input"
              autoFocus
            />
            <div className="flex items-center justify-between text-[11px] text-slate-400 -mt-2">
              <span>This stays private in your profile.</span>
              <span>{(formData.text || '').length}/1000</span>
            </div>
            <div>
              <label className="text-[12px] text-slate-500 mb-1.5 block">Category (optional)</label>
              <select
                value={formData.category || 'other'}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none text-[14px]"
                data-testid="note-category-select"
              >
                <option value="other">Other</option>
                <option value="symptom">Symptom</option>
                <option value="medication_change">Medication change</option>
                <option value="doctor_comment">Doctor comment</option>
                <option value="concern">Concern</option>
                <option value="life_context">Life context</option>
              </select>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={!(formData.text && formData.text.trim().length >= 2) || saving}
            className="w-full mt-6 bg-[#235347] text-white rounded-full px-6 py-3 font-medium hover:bg-[#1c4439] disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="save-note-btn"
          >
            {saving && <Spinner size={16} className="animate-spin" />}
            {modal.mode === 'add' ? 'Save — Kindra can use this' : 'Save changes'}
          </button>
        </BottomSheet>
      );
    }
    return null;
  }

  // ─── Tab definitions (Stage 9 polish — collapsed to 3 from 7).
  //   Snapshot · Record · Trends.  Conditions/Meds/Symptoms/Reports become
  //   subsections inside Record.  Risks + Timeline live inside Trends.
  const TABS = [
    { id: 'snapshot', label: 'Snapshot' },
    { id: 'record',   label: 'Record',
      count: (activeConditions.length + medications.length + symptoms.length) || null },
    { id: 'trends',   label: 'Trends' },
  ];

  // Stage 9: legacy ids ('conditions', 'medications', 'symptoms', 'reports')
  // map into 'record'; ('risks', 'timeline') map into 'trends'. Anything else
  // falls back to 'snapshot' so old bookmarks still land somewhere sane.
  const LEGACY_TAB_MAP = {
    conditions: 'record', medications: 'record', symptoms: 'record', reports: 'record',
    risks: 'trends', timeline: 'trends',
  };
  const effectiveTab = LEGACY_TAB_MAP[activeTab] || activeTab;

  // Build the "Top 3 things Kindra is monitoring" list from real data.
  // Priority: safety signals > top conditions > recent symptoms.
  const monitoring = (() => {
    const items = [];
    safetySignals.slice(0, 2).forEach(sig => items.push({ kind: 'risk', label: sig.title || sig.summary || 'A signal worth watching', why: sig.description || sig.summary }));
    activeConditions.slice(0, 3).forEach((c, idx) => {
      if (items.length < 3 && !items.find(i => i.label === c.name)) {
        // Stage 9 polish: rotate the "why" copy across condition cards so the
        // monitoring list never reads as 3× the same boilerplate sentence.
        const fallbackCopy = [
          'Tracked in your record — Kindra brings it into every answer.',
          'Watching this for changes that connect to how you feel.',
          'Lives in your timeline so future reports are read in context.',
        ];
        items.push({
          kind: 'condition',
          label: c.name,
          why: c.notes || fallbackCopy[idx % fallbackCopy.length],
        });
      }
    });
    symptoms.slice(0, 1).forEach(s => {
      if (items.length < 3) items.push({ kind: 'symptom', label: s.name, why: 'A symptom you flagged. Kindra tracks how it changes.' });
    });
    return items.slice(0, 3);
  })();

  // ─── Main Render ───
  return (
    <MainLayout>
      <div className="max-w-lg lg:max-w-3xl xl:max-w-5xl mx-auto pb-4" data-testid="health-page">
        {/* ─── Sticky Header ─────────────────────────── */}
        <div className="sticky top-0 z-20 bg-[#FBFAF6]/95 backdrop-blur-md -mx-4 px-4 pt-3 pb-3 border-b border-slate-100 safe-top">
          <div className="flex items-center justify-between mb-3">
            <div>
              {/* Stage 19 — reframed from "Your record" (filing cabinet) to
                  "Your health profile" (living context Kindra uses). The
                  subcopy below the tabs makes the promise explicit. */}
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.18em]">Your health profile</p>
              <h1 className="font-serif text-[26px] sm:text-[30px] leading-tight tracking-[-0.01em] text-[#0E1C2E] font-normal mt-0.5">
                This is what Kindra <em className="italic">knows about you.</em>
              </h1>
              <p className="mt-2 text-[12.5px] text-slate-500 leading-relaxed max-w-xl">
                The context Kindra uses to personalise answers, reports, and appointment prep. Update it anytime.
              </p>
              {/* Stage 20B — single-line trust addition. Tells the user
                  *why* this profile matters, not just *what* it is. */}
              <p className="mt-1 text-[11.5px] text-slate-400 leading-relaxed max-w-xl italic">
                The more Kindra knows, the more personal every answer becomes.
              </p>
            </div>
            <div className="relative">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="text-slate-400 hover:text-[#235347] transition-colors p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-slate-50"
                data-testid="export-menu-toggle"
                disabled={!!exporting}
              >
                {exporting ? <Spinner size={18} className="animate-spin" /> : <Export size={18} />}
              </button>
              {showExportMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowExportMenu(false)} />
                  <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-50 w-56 py-1.5 overflow-hidden">
                    <button onClick={handleExportPDF} className="w-full px-4 py-2.5 text-left flex items-center gap-3 hover:bg-slate-50" data-testid="export-doctor-summary">
                      <FilePdf size={18} className="text-red-400" weight="duotone" />
                      <div><p className="text-sm font-medium text-slate-800">Doctor Summary</p><p className="text-[10px] text-slate-400">PDF for appointments</p></div>
                    </button>
                    <button onClick={handleExportJSON} className="w-full px-4 py-2.5 text-left flex items-center gap-3 hover:bg-slate-50" data-testid="export-health-data">
                      <DownloadSimple size={18} className="text-blue-400" weight="duotone" />
                      <div><p className="text-sm font-medium text-slate-800">Export Data</p><p className="text-[10px] text-slate-400">Structured JSON backup</p></div>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Tabs — horizontally scrollable on mobile */}
          <div
            className="flex items-center gap-1 overflow-x-auto -mx-1 px-1 pb-1"
            style={{ scrollbarWidth: 'none' }}
            data-testid="health-tabs"
          >
            <style>{`[data-testid="health-tabs"]::-webkit-scrollbar{display:none}`}</style>
            {TABS.map(t => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`flex-shrink-0 px-3.5 py-2 text-[12.5px] font-medium rounded-full transition-colors flex items-center gap-1.5 ${
                  activeTab === t.id
                    ? 'bg-[#0E1C2E] text-white'
                    : 'bg-white text-[#0E1C2E] border border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`health-tab-${t.id}`}
              >
                {t.label}
                {t.count != null && t.count > 0 && (
                  <span className={`text-[10px] px-1.5 rounded-full ${activeTab === t.id ? 'bg-white/20' : 'bg-slate-100'}`}>
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Stage 19 — Profile actions row. Always visible regardless of tab.
            Founder feedback: users didn't know how to add conditions/meds/notes
            post-onboarding. These 6 actions are now the first thing after the
            header, on every tab, mobile and desktop. */}
        <section
          className="mt-5 -mx-1 px-1"
          data-testid="profile-actions"
          aria-label="Update your health profile"
        >
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            <button
              onClick={() => openAdd('condition')}
              className="group flex flex-col items-center justify-center gap-1.5 bg-white rounded-xl border border-slate-200 hover:border-[#235347]/40 hover:bg-[#235347]/[0.03] transition-colors py-3 px-2 text-center min-h-[72px]"
              data-testid="profile-action-add-condition"
            >
              <Plus size={16} className="text-[#235347] group-hover:scale-110 transition-transform" />
              <span className="text-[11.5px] font-medium text-[#0E1C2E] leading-tight">Add<br/>condition</span>
            </button>
            <button
              onClick={() => openAdd('medication')}
              className="group flex flex-col items-center justify-center gap-1.5 bg-white rounded-xl border border-slate-200 hover:border-[#235347]/40 hover:bg-[#235347]/[0.03] transition-colors py-3 px-2 text-center min-h-[72px]"
              data-testid="profile-action-add-medication"
            >
              <Plus size={16} className="text-[#235347] group-hover:scale-110 transition-transform" />
              <span className="text-[11.5px] font-medium text-[#0E1C2E] leading-tight">Add<br/>medication</span>
            </button>
            <button
              onClick={() => openAdd('symptom')}
              className="group flex flex-col items-center justify-center gap-1.5 bg-white rounded-xl border border-slate-200 hover:border-[#235347]/40 hover:bg-[#235347]/[0.03] transition-colors py-3 px-2 text-center min-h-[72px]"
              data-testid="profile-action-add-symptom"
            >
              <Plus size={16} className="text-[#235347] group-hover:scale-110 transition-transform" />
              <span className="text-[11.5px] font-medium text-[#0E1C2E] leading-tight">Add<br/>symptom</span>
            </button>
            <button
              onClick={() => navigate('/documents')}
              className="group flex flex-col items-center justify-center gap-1.5 bg-white rounded-xl border border-slate-200 hover:border-[#235347]/40 hover:bg-[#235347]/[0.03] transition-colors py-3 px-2 text-center min-h-[72px]"
              data-testid="profile-action-add-report"
            >
              <Plus size={16} className="text-[#235347] group-hover:scale-110 transition-transform" />
              <span className="text-[11.5px] font-medium text-[#0E1C2E] leading-tight">Add<br/>report</span>
            </button>
            <button
              onClick={() => navigate('/appointments')}
              className="group flex flex-col items-center justify-center gap-1.5 bg-white rounded-xl border border-slate-200 hover:border-[#235347]/40 hover:bg-[#235347]/[0.03] transition-colors py-3 px-2 text-center min-h-[72px]"
              data-testid="profile-action-add-appointment"
            >
              <Plus size={16} className="text-[#235347] group-hover:scale-110 transition-transform" />
              <span className="text-[11.5px] font-medium text-[#0E1C2E] leading-tight">Add<br/>appointment</span>
            </button>
            <button
              onClick={() => openAdd('note')}
              className="group flex flex-col items-center justify-center gap-1.5 bg-[#235347]/[0.08] rounded-xl border border-[#235347]/25 hover:bg-[#235347]/[0.12] transition-colors py-3 px-2 text-center min-h-[72px]"
              data-testid="profile-action-add-note"
            >
              <ChatCircleText size={16} className="text-[#235347] group-hover:scale-110 transition-transform" />
              <span className="text-[11.5px] font-medium text-[#0E1C2E] leading-tight">Tell Kindra<br/>something</span>
            </button>
          </div>
        </section>

        {/* ─── Tab content ─────────────────────────── */}
        <div className="mt-6 space-y-6">

        {/* ═══ SNAPSHOT TAB ═══ */}
        {effectiveTab === 'snapshot' && (
          <>
            <HealthStatusCard
              status={status}
              topSignals={topSignals}
              conditionCount={activeConditions.length}
              medCount={medications.length}
              loading={loading}
            />

            {/* Stage 19 — Notes card in Snapshot. Shows the most recent
                3 notes the user asked Kindra to remember. Clear empty state
                that explains WHY notes matter. Each note is editable and
                deletable inline. */}
            <section data-testid="snapshot-notes">
              <div className="flex items-center justify-between mb-2.5">
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.18em]">
                  Important notes for Kindra
                </p>
                {notes.length > 0 && (
                  <button
                    onClick={() => openAdd('note')}
                    className="text-[11px] font-medium text-[#235347] hover:text-[#1c4439] inline-flex items-center gap-1"
                    data-testid="notes-add-inline"
                  >
                    <Plus size={12} /> Add another
                  </button>
                )}
              </div>
              {notes.length === 0 ? (
                <button
                  onClick={() => openAdd('note')}
                  className="w-full text-left bg-[#FBFAF6] rounded-xl border border-dashed border-[#235347]/30 hover:border-[#235347]/50 hover:bg-[#235347]/[0.03] transition-colors p-4"
                  data-testid="notes-empty-cta"
                >
                  <p className="text-[13.5px] font-medium text-[#0E1C2E] mb-1">Tell Kindra something that matters.</p>
                  <p className="text-[12.5px] text-slate-500 leading-relaxed">
                    A new symptom, a recent change, a family history detail, something your doctor said. Kindra uses this context to give more personal answers.
                  </p>
                  <span className="mt-2 inline-flex items-center gap-1 text-[12px] font-medium text-[#235347]">
                    <Plus size={13} /> Add a note
                  </span>
                </button>
              ) : (
                <div className="space-y-2">
                  {notes.slice(0, 3).map((n) => (
                    <div
                      key={n.id}
                      className="bg-white rounded-xl border border-slate-200 p-3.5 flex items-start gap-3"
                      data-testid={`note-card-${n.id}`}
                    >
                      <Notebook size={14} className="text-[#235347] flex-shrink-0 mt-[3px]" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[13.5px] text-[#0E1C2E] leading-relaxed whitespace-pre-wrap break-words">{n.text}</p>
                        <p className="mt-1 text-[10.5px] text-slate-400 uppercase tracking-[0.12em]">
                          {(n.category || 'other').replace(/_/g, ' ')}
                          {n.created_at && (
                            <span className="ml-2 normal-case tracking-normal">
                              · {new Date(n.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => { setFormData({ text: n.text, category: n.category || 'other' }); setModal({ type: 'note', mode: 'edit', id: n.id }); }}
                          className="p-1.5 hover:bg-slate-50 rounded-md"
                          data-testid={`edit-note-${n.id}`}
                          aria-label="Edit note"
                        >
                          <PencilSimple size={14} className="text-slate-400" />
                        </button>
                        <button
                          onClick={() => handleDelete('note', n.id)}
                          className="p-1.5 hover:bg-red-50 rounded-md"
                          data-testid={`delete-note-${n.id}`}
                          aria-label="Delete note"
                        >
                          <Trash size={14} className="text-red-400" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {notes.length > 3 && (
                    <p className="text-[11.5px] text-slate-400 mt-1 pl-1">
                      + {notes.length - 3} more — visible to Kindra when you chat.
                    </p>
                  )}
                </div>
              )}
            </section>

            {/* Top 3 things Kindra is monitoring */}
            {monitoring.length > 0 && (
              <section data-testid="snapshot-monitoring">
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.18em] mb-2.5">
                  Top {monitoring.length} thing{monitoring.length !== 1 ? 's' : ''} Kindra is monitoring
                </p>
                <div className="space-y-2">
                  {monitoring.map((m, i) => (
                    <button
                      key={i}
                      onClick={() => navigate('/chat', { state: { seedQuery: `Tell me more about ${m.label}` } })}
                      className="w-full text-left bg-white rounded-xl border border-slate-200 hover:border-slate-300 transition-colors p-4 flex items-start gap-3"
                      data-testid={`snapshot-monitor-${m.kind}`}
                    >
                      <span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${
                        m.kind === 'risk' ? 'bg-amber-400' : m.kind === 'condition' ? 'bg-emerald-500' : 'bg-rose-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-[14.5px] font-medium text-[#0E1C2E] leading-snug">{m.label}</p>
                        {m.why && (
                          <p className="text-[12px] text-slate-500 mt-1 leading-relaxed line-clamp-2">{m.why}</p>
                        )}
                      </div>
                      <CaretRight size={14} className="text-slate-300 flex-shrink-0 mt-1" />
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Sprint C — Last upload + reports CTA (always shown, adapts copy). */}
            <ReportStatusCard status={healthState?.report_status} />

            {/* Understand-better CTA — surfaces /learn */}
            {!loading && (activeConditions.length > 0 || medications.length > 0) && (
              <button
                onClick={() => {
                  const topCondition = activeConditions[0]?.name || '';
                  const topMed = medications[0]?.name || '';
                  const topic = topCondition && topMed ? `How ${topMed} works with my ${topCondition}` : topCondition ? `Understand my ${topCondition}` : `Understand my ${topMed} treatment`;
                  navigate('/learn', { state: { prefillCourse: topic } });
                }}
                className="w-full flex items-center gap-3 bg-[#235347]/5 border border-[#235347]/12 rounded-xl px-4 py-3 hover:bg-[#235347]/8 transition-colors text-left"
                data-testid="understand-better-cta"
              >
                <Notebook size={16} className="text-[#235347]" weight="duotone" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#235347]">Ask Kindra to teach you about this</p>
                  <p className="text-[11px] text-slate-400">Personalised lesson grounded in your record</p>
                </div>
                <CaretRight size={12} className="text-slate-300 flex-shrink-0" />
              </button>
            )}

            {/* P1 Stage-14b — quiet "View details" toggle. Snapshot stays calm
                by default; the analytical narrative + clinician question +
                next-steps plan are still here for power users. */}
            {(((healthState?.drivers || []).length > 0) || healthState?.doctor_question || planItems.length > 0) && (
              <div className="pt-1">
                <button
                  onClick={() => setShowSnapshotDetails(v => !v)}
                  className="text-[12.5px] text-slate-500 hover:text-[#0E1C2E] transition-colors inline-flex items-center gap-1.5"
                  data-testid="snapshot-details-toggle"
                >
                  {showSnapshotDetails ? 'Hide details' : 'View details'}
                  <span className="text-slate-400 text-[10px]">{showSnapshotDetails ? '▲' : '▼'}</span>
                </button>
              </div>
            )}

            {showSnapshotDetails && (
              <>
                {/* Sprint C — What may be driving how you feel (intelligence narrative). */}
                <DriversSection drivers={healthState?.drivers || []} />

                {/* Sprint C — One smart question for the doctor. */}
                <DoctorQuestionCard question={healthState?.doctor_question} />

                {planItems.length > 0 && (
                  <section data-testid="next-steps">
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.18em] mb-2.5">
                      Next steps from Kindra <span className="text-slate-300">· {planItems.length}</span>
                    </p>
                    <div className="space-y-2">
                      {planItems.slice(0, 5).map(item => (
                        <div key={item.id} className="bg-white border border-slate-200 rounded-xl p-3.5 flex items-start gap-3" data-testid={`plan-item-${item.id}`}>
                          <button onClick={() => handleCompletePlanItem(item.id)} className="mt-0.5 p-0.5 flex-shrink-0 min-w-[24px] min-h-[24px] flex items-center justify-center" data-testid={`complete-plan-${item.id}`}>
                            <Circle size={18} className="text-emerald-400" />
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-700 leading-relaxed">{item.text}</p>
                            {item.topic_title && <p className="text-[11px] text-slate-400 mt-0.5">From: {item.topic_title}</p>}
                          </div>
                          <button onClick={() => handleRemovePlanItem(item.id)} className="p-1 flex-shrink-0 hover:bg-slate-50 rounded min-w-[28px] min-h-[28px] flex items-center justify-center" data-testid={`remove-plan-${item.id}`}>
                            <X size={12} className="text-slate-300" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </>
            )}
          </>
        )}

        {/* ═══ TRENDS TAB (Stage 9: merges Risks + Timeline) ═══ */}
        {effectiveTab === 'trends' && (
          <section data-testid="trends-tab" className="space-y-8">
            {/* Risks sub-section */}
            <div data-testid="trends-risks">
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.18em] mb-2.5">
                What needs attention
              </p>
              {safetySignals.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-2xl px-6 py-12 text-center" data-testid="risks-empty">
                  <p className="font-serif italic text-[20px] text-[#0E1C2E] mb-2">All clear right now.</p>
                  <p className="text-[13px] text-slate-500 leading-relaxed mb-4">Kindra hasn't flagged anything that needs your attention.</p>
                  <p className="text-[12px] text-slate-400 leading-relaxed italic max-w-[280px] mx-auto">
                    Upload a recent report or keep checking in and Kindra will flag anything worth watching.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {safetySignals.map(sig => <AttentionCard key={sig.id} signal={sig} onDismiss={handleDismissSignal} />)}
                </div>
              )}
            </div>
            {/* Timeline sub-section */}
            <div data-testid="trends-timeline">
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.18em] mb-3">Timeline</p>
              <TimelineSection />
            </div>
          </section>
        )}

        {/* ═══ RECORD TAB (Stage 9: merges Conditions + Meds + Symptoms + Reports) ═══ */}
        {effectiveTab === 'record' && (
          <section data-testid="record-tab" className="space-y-8">
            <div data-testid="record-conditions">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.18em]">Conditions <span className="text-slate-300">· {activeConditions.length}</span></p>
                <button onClick={() => openAdd('condition')} className="text-[12px] text-[#0E1C2E] font-medium" data-testid="add-condition-btn">+ Add</button>
              </div>
              <SetupCard icon={Heart} iconColor="text-[#235347]" label="Conditions" count={activeConditions.length} items={conditionItems} onEdit={() => setEditSection('conditions')} onAdd={() => openAdd('condition')} testId="setup-conditions" forceFullWidth />
            </div>

            <div data-testid="record-medications">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.18em]">Medications <span className="text-slate-300">· {medications.length}</span></p>
                <button onClick={() => openAdd('medication')} className="text-[12px] text-[#0E1C2E] font-medium" data-testid="add-medication-btn">+ Add</button>
              </div>
              <SetupCard icon={Pill} iconColor="text-blue-500" label="Medications" count={medications.length} items={medicationItems} onEdit={() => setEditSection('medications')} onAdd={() => openAdd('medication')} testId="setup-medications" forceFullWidth />
            </div>

            <div data-testid="record-symptoms">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.18em]">Symptoms <span className="text-slate-300">· {symptoms.length}</span></p>
                <button onClick={() => openAdd('symptom')} className="text-[12px] text-[#0E1C2E] font-medium" data-testid="add-symptom-btn">+ Add</button>
              </div>
              <SetupCard icon={Heartbeat} iconColor="text-rose-500" label="Symptoms" count={symptoms.length} items={symptomItems} onEdit={() => setEditSection('symptoms')} onAdd={() => openAdd('symptom')} testId="setup-symptoms" forceFullWidth />
            </div>

            <div data-testid="record-reports">
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.18em] mb-3">Reports & uploads</p>
              <button
                onClick={() => navigate('/documents')}
                className="w-full bg-white border border-slate-200 rounded-xl p-5 hover:border-slate-300 transition-colors text-left flex items-center gap-4"
                data-testid="reports-go-to-docs"
              >
                <FilePdf size={24} className="text-[#0E1C2E]" weight="duotone" />
                <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-medium text-[#0E1C2E]">Manage your reports</p>
                  <p className="text-[12.5px] text-slate-500 mt-0.5">Blood tests, scans, specialist letters — Kindra reads them and remembers.</p>
                </div>
                <CaretRight size={14} className="text-slate-300" />
              </button>
              <p className="text-[12px] text-slate-500 italic leading-relaxed mt-3 px-1">
                Every report you upload makes Kindra's answers more accurate. They stay private to you and live in your timeline.
              </p>
            </div>
          </section>
        )}

        </div>
      </div>

      {/* ═══ CRUD Modals ═══ */}
      <AnimatePresence>
        {renderModal()}
      </AnimatePresence>

      {/* Send Health Summary Email Modal */}
      <AnimatePresence>
        {showEmailModal && (
          <SendEmailModal
            reportType="health_summary"
            reportId="current"
            defaultSubject="Health Summary"
            onClose={() => setShowEmailModal(false)}
          />
        )}
      </AnimatePresence>
    </MainLayout>
  );
};

export default HealthPage;

